import type { TrackingLink, ThemeClasses } from "../types";
import {
  getStatusColor,
  getStatusText,
  getAccessTypeText,
  getAccessTypeIcon,
  getAccessTypeColor,
  isExpiringSoon,
  trackingStatusKey,
} from "../utils/helpers";
import { useRealTimeCountdown } from "../hooks/useRealTimeCountdown";
import { useState, useRef, useEffect } from "react";

interface TrackingLinkGridCardProps {
  trackingLink: TrackingLink;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onEdit: (trackingLink: TrackingLink) => void;
  onDelete: (trackingLink: TrackingLink) => void;
  onCopy: (trackingLink: TrackingLink) => void;
  onActivate: (trackingLink: TrackingLink) => void;
  onExtend: (trackingLink: TrackingLink) => void;
  onDetails: (trackingLink: TrackingLink) => void;
  onRemove?: (trackingLink: TrackingLink) => void;
  loadingDetails?: boolean;
  loadingActions?: Record<string, boolean>;
}

export function TrackingLinkGridCard({
  trackingLink,
  themeClasses,
  onEdit,
  onDelete,
  onCopy,
  onActivate,
  onExtend,
  onDetails,
  onRemove,
  loadingDetails,
  loadingActions,
}: TrackingLinkGridCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const expiringSoon = isExpiringSoon(trackingLink.expirationDate);
  const statusKey = trackingStatusKey(trackingLink.status);
  const remainingTime = useRealTimeCountdown(trackingLink.expirationDate, statusKey);
  const isPending = statusKey === "pending";
  const isExpired = statusKey === "expired";
  const isInactive =
    statusKey === "inactive" ||
    trackingLink.activationType === "inactive";
  const hasNoUnits = !trackingLink.units || trackingLink.units.length === 0;

  // Loading states for individual actions
  const isLoadingActivate = loadingActions?.[`activate-${trackingLink.id}`] || false;
  const isLoadingEdit = loadingActions?.[`edit-${trackingLink.id}`] || false;
  const isLoadingDelete = loadingActions?.[`delete-${trackingLink.id}`] || false;
  const isLoadingRemove = loadingActions?.[`remove-${trackingLink.id}`] || false;
  const isLoadingExtend = loadingActions?.[`extend-${trackingLink.id}`] || false;
  const isLoadingDetails = loadingActions?.[`details-${trackingLink.id}`] || false;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const renderActionButtons = () => {
    if (isInactive) {
      return (
        <button
          onClick={() => onActivate(trackingLink)}
          disabled={isLoadingActivate}
          className="w-full flex-1 py-2 px-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoadingActivate ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              <span>Activando...</span>
            </>
          ) : (
            <>
              <i className="ri-refresh-line"></i>
              <span>Activar link</span>
            </>
          )}
        </button>
      );
    }

    if (isPending || hasNoUnits) {
      return (
        <button
          onClick={() => onEdit(trackingLink)}
          disabled={isLoadingEdit}
          className="w-full flex-1 py-2 px-3 bg-primary-500 hover:bg-primary-400 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoadingEdit ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              <span>Configurando...</span>
            </>
          ) : (
            <>
              <i className="ri-settings-3-line"></i>
              <span>Configurar</span>
            </>
          )}
        </button>
      );
    }

    if (isExpired) {
      return (
        <>
          <button
            onClick={() => onExtend(trackingLink)}
            disabled={isLoadingExtend}
            className={`w-full flex-1 py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoadingExtend ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                <span>Renovando...</span>
              </>
            ) : (
              <>
                <i className="ri-refresh-line"></i>
                <span>Renovar</span>
              </>
            )}
          </button>
          <button
            onClick={() => onDelete(trackingLink)}
            disabled={isLoadingDelete}
            className="w-full py-2 px-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingDelete ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                <span>Desactivando...</span>
              </>
            ) : (
              <>
                <i className="ri-close-circle-line"></i>
                <span>Desactivar</span>
              </>
            )}
          </button>
        </>
      );
    }

    return (
      <div className="w-full flex flex-col gap-1">
        {/* Botones superiores */}
        <div className="flex gap-1">
          <button
            onClick={() => onCopy(trackingLink)}
            className="flex-1 py-1.5 px-2 bg-primary-500 hover:bg-primary-400 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1"
          >
            <i className="ri-file-copy-line text-base"></i>
            <span>Copiar</span>
          </button>
          <button
            onClick={() => onDetails(trackingLink)}
            disabled={loadingDetails || isLoadingDetails}
            className={`flex-1 py-1.5 px-2 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-md ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loadingDetails || isLoadingDetails ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base"></i>
                <span>Cargando...</span>
              </>
            ) : (
              <>
                <i className="ri-eye-line text-base"></i>
                <span>Detalles</span>
              </>
            )}
          </button>
        </div>

        {/* Editar y Desact abajo en lg, horizontales en 2xl */}
        <div className="flex gap-1 2xl:flex-1">
          <button
            onClick={() => onEdit(trackingLink)}
            disabled={isLoadingEdit}
            className={`flex-1 py-1.5 px-1 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-md ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors flex items-center justify-center gap-0.5 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoadingEdit ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base"></i>
                <span>Editando...</span>
              </>
            ) : (
              <>
                <i className="ri-pencil-line text-base"></i>
                <span>Editar</span>
              </>
            )}
          </button>
          <button
            onClick={() => onDelete(trackingLink)}
            disabled={isLoadingDelete}
            className="flex-1 py-1.5 px-1 bg-red-50 hover:bg-red-100 text-red-600 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingDelete ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base"></i>
                <span>Desactivando...</span>
              </>
            ) : (
              <>
                <i className="ri-close-circle-line text-base"></i>
                <span>Desactivar</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-3 sm:p-4 lg:p-5 hover:shadow-lg transition-all relative`}
    >
      {/* Menu button */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setShowMenu(!showMenu)}
          className={`absolute top-4 right-4 p-1 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText}`}
        >
          <i className="ri-more-2-fill"></i>
        </button>

        {/* Dropdown menu */}
        {showMenu && (
          <div className={`absolute top-10 right-0 w-48 ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg shadow-lg z-50`}>
            <div className="py-1">
              <button
                onClick={() => {
                  onEdit(trackingLink);
                  setShowMenu(false);
                }}
                disabled={isLoadingEdit}
                className={`w-full px-4 py-2 text-left text-sm ${themeClasses.text} hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoadingEdit ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>Editando...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-pencil-line"></i>
                    <span>Editar</span>
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  onCopy(trackingLink);
                  setShowMenu(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm ${themeClasses.text} hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2`}
              >
                <i className="ri-file-copy-line"></i>
                <span>Copiar link</span>
              </button>
              {!isInactive && !isExpired && (
                <button
                  onClick={() => {
                    onDetails(trackingLink);
                    setShowMenu(false);
                  }}
                  disabled={loadingDetails || isLoadingDetails}
                  className={`w-full px-4 py-2 text-left text-sm ${themeClasses.text} hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loadingDetails || isLoadingDetails ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Cargando...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-eye-line"></i>
                      <span>Ver detalles</span>
                    </>
                  )}
                </button>
              )}
              {isExpired && (
                <button
                  onClick={() => {
                    onExtend(trackingLink);
                    setShowMenu(false);
                  }}
                  disabled={isLoadingExtend}
                  className={`w-full px-4 py-2 text-left text-sm ${themeClasses.text} hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isLoadingExtend ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Renovando...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-refresh-line"></i>
                      <span>Renovar</span>
                    </>
                  )}
                </button>
              )}
              {!isInactive && (
                <button
                  onClick={() => {
                    onDelete(trackingLink);
                    setShowMenu(false);
                  }}
                  disabled={isLoadingDelete}
                  className={`w-full px-4 py-2 text-left text-sm text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isLoadingDelete ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Desactivando...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-close-circle-line"></i>
                      <span>Desactivar</span>
                    </>
                  )}
                </button>
              )}
              {onRemove && (
                <button
                  onClick={() => {
                    onRemove(trackingLink);
                    setShowMenu(false);
                  }}
                  disabled={isLoadingRemove}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingRemove ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Eliminando...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-delete-bin-line"></i>
                      <span>Eliminar</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="mb-3 sm:mb-4 pr-6">
        <h3
          className={`font-semibold ${themeClasses.text} text-base sm:text-lg truncate`}
        >
          {trackingLink.name}
        </h3>
        <div className="flex items-center space-x-2 mt-2">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
              trackingLink.status,
            )}`}
          >
            {getStatusText(trackingLink.status)}
          </span>
          <span
            className={`px-2  rounded-full text-xs text-[#4B5563] font-medium flex items-center space-x-1 ${getAccessTypeColor(
              trackingLink.accessType,
            )}`}
          >
            <i
              className={`${getAccessTypeIcon(trackingLink.accessType)} text-base text-[#4B5563]`}
            ></i>
            <span>{getAccessTypeText(trackingLink.accessType)}</span>
          </span>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
        <div className="flex items-center justify-between">
          <span className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            {isExpired ? "Expiró hace" : isPending ? "Vigencia" : "Tiempo restante"}
          </span>
          <span
            className={`text-xs sm:text-sm font-mono font-medium ${
              remainingTime.isWarning || expiringSoon
                ? "text-amber-500"
                : isExpired
                  ? "text-red-500"
                  : themeClasses.text
            }`}
          >
            {remainingTime.text}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            Unidades
          </span>
          <span
            className={`text-xs sm:text-sm font-medium ${themeClasses.text}`}
          >
            {trackingLink.unitsCount > 0
              ? `${trackingLink.unitsCount} vehículo${trackingLink.unitsCount > 1 ? "s" : ""}`
              : isPending
                ? "0 seleccionadas"
                : "0 seleccionadas"}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
            Aperturas
          </span>
          <span
            className={`text-xs sm:text-sm font-medium ${themeClasses.text}`}
          >
            {trackingLink.accessCount > 0
              ? `${trackingLink.accessCount.toLocaleString()} veces`
              : "—"}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-2">{renderActionButtons()}</div>
    </div>
  );
}
